export type EmailTemplateItem = {
  id: string;
  key: string;
  category?: string;
  subjectTemplate?: string;
  htmlTemplate?: string;
  textTemplate?: string;
  placeholders?: string[];
  version?: number;
  disabled?: boolean;
  updatedAt?: string;
  createdAt?: string;
};

function authHeaders() {
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  return token ? { Authorization: `Bearer ${token}` } : {} as any;
}

export async function listEmailTemplates(): Promise<{ items: EmailTemplateItem[]; total: number }> {
  const res = await fetch('/api/admin/email-templates', { credentials: 'same-origin', headers: { ...authHeaders() } });
  const body = await res.json().catch(()=>({ items: [], total: 0 }));
  if (!res.ok) throw new Error(body?.error || 'Failed to list email templates');
  const items = Array.isArray(body?.items) ? body.items.map((t:any) => ({
    id: String(t._id || t.id), key: t.key,
    category: t.category,
    subjectTemplate: t.subjectTemplate,
    htmlTemplate: t.htmlTemplate,
    textTemplate: t.textTemplate,
    placeholders: Array.isArray(t.placeholders) ? t.placeholders : [],
    version: t.version,
    disabled: !!t.disabled,
    updatedAt: t.updatedAt,
    createdAt: t.createdAt,
  })) : [];
  return { items, total: Number(body?.total || items.length) };
}

export async function createEmailTemplate(payload: Partial<EmailTemplateItem>): Promise<{ id: string; key: string }> {
  // Allow server to auto-generate key when omitted
  const { key, ...rest } = payload;
  const bodyPayload = key ? { key, ...rest } : rest;
  const res = await fetch('/api/admin/email-templates', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify(bodyPayload) });
  const body = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(body?.error || 'Failed to create email template');
  return body as { id: string; key: string };
}

export async function updateEmailTemplate(id: string, updates: Partial<EmailTemplateItem>): Promise<{ ok: boolean; id: string }> {
  const res = await fetch(`/api/admin/email-templates/${encodeURIComponent(id)}`, { method: 'PATCH', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify(updates) });
  const body = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(body?.error || 'Failed to update email template');
  return body as any;
}

export async function deleteEmailTemplate(id: string): Promise<boolean> {
  const res = await fetch(`/api/admin/email-templates/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'same-origin', headers: { ...authHeaders() } });
  const body = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(body?.error || 'Failed to delete email template');
  return true;
}

export async function renderEmailTemplate(id: string, placeholders?: Record<string, any>, format?: 'json'|'html'|'text'|'subject') {
  const q = new URLSearchParams();
  if (placeholders) q.set('placeholders', JSON.stringify(placeholders));
  if (format && format !== 'json') q.set('format', format);
  const res = await fetch(`/api/admin/email-templates/${encodeURIComponent(id)}/render${q.toString() ? `?${q}` : ''}`, { credentials: 'same-origin', headers: { ...authHeaders() } });
  const ctype = res.headers.get('content-type') || '';
  if (format === 'html') return await res.text();
  if (format === 'text' || format === 'subject') return await res.text();
  const body = /application\/json/.test(ctype) ? await res.json() : { html: await res.text() };
  if (!res.ok) throw new Error((body as any)?.error || 'Failed to render email template');
  return body;
}

export async function listEmailTemplatePlaceholders(): Promise<Record<string, string[]>> {
  const res = await fetch('/api/admin/email-templates/placeholders', { credentials: 'same-origin', headers: { ...authHeaders() } });
  const body = await res.json().catch(()=>({ items: {} }));
  if (!res.ok) throw new Error(body?.error || 'Failed to load placeholders');
  return body.items || {};
}

export async function testSendEmailTemplate(id: string, to: string, placeholders?: Record<string, any>): Promise<{ ok: boolean; provider?: string; skipped?: boolean }> {
  const res = await fetch(`/api/admin/email-templates/${encodeURIComponent(id)}/test-send`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ to, placeholders }) });
  const body = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(body?.error || 'Test send failed');
  return body as any;
}
